import type { Logger } from "@expert/logging";
import type { KyInstance } from "ky";

import type { SalesAnalyticsDispatcher } from "../sales-context/SalesContext.types";

export type SendNotificationPayload = {
    sessionId: string;
    partner: string;
    productOfferId: string;
    productSku: string;
    customerEmailId?: string;
    customerMdn?: string;
    customerChatId?: string;
    workerSid?: string;
    topic?: string;
    language?: string;
    guidedBrowsing?: boolean;
};

export type SendNotificationResponse = {
    maxAttemptsReached: boolean;
    currentAttempt: number;
    // TEMP: smartlink returned so the expert's GuidedBrowsingModal iframe can mirror the customer's
    // session. Surfacing the customer link to the UI is a known security smell — remove once the
    // modal sources its URL out-of-band (e.g., via a denatured, expert-only version of the link).
    url?: string;
};

export async function sendNotification(
    client: KyInstance,
    logger: Logger,
    dispatcher: SalesAnalyticsDispatcher,
    payload: SendNotificationPayload,
): Promise<SendNotificationResponse> {
    if (!payload.customerEmailId && !payload.customerMdn) {
        throw new Error("Either customerEmailId or customerMdn must be provided");
    }
    const childLogger = logger.child({ module: "sendNotification" });

    childLogger.info({ payload }, "Sending notification");

    try {
        const response = await client.post("v1/send-notification", { json: payload });
        // NOTE: effectively dead with the real client. ky's `throwHttpErrors` defaults to true, so a
        // 4xx/5xx throws `HTTPError` from `.post(...)` above and lands in the catch — this `!response.ok`
        // branch is never reached unless a caller passes a client built with `throwHttpErrors: false`.
        // Proven by sendNotification.integration.test.ts ("...ky throws HTTPError before the response.ok
        // check is reached"). Left in place deliberately; remove only alongside that test.
        if (!response.ok) {
            throw new Error(`Failed to send notification: ${response.status} ${response.statusText}`);
        }
        const data = await response.json<SendNotificationResponse>();
        if (data === null) {
            throw new Error("Received null response from notification service");
        }
        void dispatcher.dispatchBusinessEvent("NotificationSent");
        return data;
    } catch (error) {
        void dispatcher.dispatchBusinessEvent("NotificationSendFailure");
        childLogger.warn({ error }, "Error sending notification");
        throw error;
    }
}
